import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  MdDirectionsCar,
  MdPerson,
  MdLocationOn,
  MdPhone,
  MdSearch,
  MdAdd,
  MdEdit,
  MdDelete,
  MdGridView,
  MdViewList,
  MdRefresh,
  MdCheckCircle,
  MdAirlineSeatReclineNormal,
  MdAcUnit,
  MdLocalGasStation,
  MdRadar,
  MdVisibility,
  MdPlayArrow,
  MdPause,
  MdCalculate,
  MdVerified,
  MdAssignmentInd,
  MdSend,
} from "react-icons/md";
import "./VendorCabs.css";

/* ==================== CONFIG ==================== */
const API = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api/vendor/cabs`
  : "https://bmtvendorvendorfinal.onrender.com/api/vendor/cabs";

const PAGE_SIZE = 10;
const MAX_GALLERY = 10;

const CAB_TYPES = [
  "Sedan",
  "Mini",
  "SUV",
  "Premium Sedan",
  "Premium SUV",
  "Luxury",
  "Tempo Traveller",
  "Electric",
];

const CAB_TYPE_ICONS = {
  Mini: "🚗",
  Sedan: "🚙",
  SUV: "🚐",
  "Premium Sedan": "🚘",
  "Premium SUV": "🚙",
  Luxury: "🏎️",
  "Tempo Traveller": "🚌",
  Electric: "⚡",
};

const BASE_RATES = {
  Mini: 250,
  Sedan: 350,
  SUV: 550,
  "Premium Sedan": 600,
  "Premium SUV": 850,
  Luxury: 1200,
  "Tempo Traveller": 1400,
  Electric: 400,
};

const PER_KM_RATES = {
  Mini: 12,
  Sedan: 15,
  SUV: 20,
  "Premium Sedan": 22,
  "Premium SUV": 28,
  Luxury: 45,
  "Tempo Traveller": 32,
  Electric: 14,
};

const FILTER_PILLS = [
  { id: "all", label: "All Fleet" },
  { id: "available", label: "🟢 Available Now" },
  { id: "40km", label: "🎯 40km Hub" },
  { id: "driver", label: "👨‍✈️ Driver Assigned" },
  { id: "sedan", label: "🚙 Sedan" },
  { id: "suv", label: "🚐 SUV" },
  { id: "mini", label: "🚗 Mini" },
];

const FORM_TABS = [
  { id: "vehicle", label: "🚗 1. Vehicle Specs" },
  { id: "driver", label: "👨‍✈️ 2. Driver Details" },
  { id: "location", label: "📍 3. Location (Within 40km)" },
  { id: "pricing", label: "💰 4. Photos & Pricing" },
];

// which tab each field lives on (used to jump to the tab that has an error)
const FIELD_TAB = {
  vehicleNumber: "vehicle",
  cabType: "vehicle",
  totalSeats: "vehicle",
  availableSeats: "vehicle",
  modelYear: "vehicle",
  driverName: "driver",
  driverPhone: "driver",
  driverLicenseNumber: "driver",
  driverExperience: "driver",
  baseLocation: "location",
  fromCity: "location",
  price: "pricing",
  pricePerKm: "pricing",
};

const makeInitialForm = () => ({
  // 1. Vehicle
  cabType: "Sedan",
  vehicleNumber: "",
  brand: "",
  model: "",
  modelYear: new Date().getFullYear().toString(),
  vehicleColor: "",
  totalSeats: "4",
  availableSeats: "4",
  acType: "AC",
  fuelType: "Petrol",

  // 2. Driver
  driverName: "",
  driverPhone: "",
  driverAlternatePhone: "",
  driverLicenseNumber: "",
  driverExperience: "",

  // 3. Location
  baseLocation: "",
  pickupLocation: "",
  fromCity: "",
  toCity: "",
  serviceRadiusKm: "40",
  operatingArea: "",

  // 4. Pricing
  price: "",
  pricePerKm: "14",
  pickupTime: "09:00",
});

/* ==================== HELPERS ==================== */
const getDriver = (cab) => ({
  name: cab.driver?.name || cab.driverName || "",
  phone: cab.driver?.phone || cab.driverPhone || "",
  alternatePhone: cab.driver?.alternatePhone || cab.driverAlternatePhone || "",
  license: cab.driver?.licenseNumber || cab.driverLicenseNumber || "",
  exp: cab.driver?.experienceYears || cab.driverExperience || "",
});

const isCabLive = (cab) => cab.isActive !== false && (cab.availableSeats || 0) > 0;

const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

const errMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

function Field({ label, error, children }) {
  return (
    <div className={`vc-form-group ${error ? "vc-has-error" : ""}`}>
      <label>{label}</label>
      {children}
      {error && <small className="vc-field-error">{error}</small>}
    </div>
  );
}

/* ==================== MAIN ==================== */
export default function VendorCabs() {
  const token =
    localStorage.getItem("vendorToken") || localStorage.getItem("token");

  // Main UI Mode: 'fleet' | 'radar' | 'estimator'
  const [activeMainTab, setActiveMainTab] = useState("fleet");

  const [cabs, setCabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'cards'
  const [sortBy, setSortBy] = useState("default");
  const [page, setPage] = useState(1);

  // Notice (success / error banner)
  const [notice, setNotice] = useState(null); // { type, text }

  // Full page Add / Edit form
  const [showForm, setShowForm] = useState(false);
  const [formTab, setFormTab] = useState("vehicle");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(makeInitialForm);
  const [errors, setErrors] = useState({});

  // Images
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingGallery, setExistingGallery] = useState([]); // saved urls
  const [newGallery, setNewGallery] = useState([]); // { file, url }

  // Quick view dossier
  const [selectedCab, setSelectedCab] = useState(null);

  // Quick driver assign modal
  const [driverModalCab, setDriverModalCab] = useState(null);
  const [driverForm, setDriverForm] = useState({
    name: "",
    phone: "",
    alternatePhone: "",
    licenseNumber: "",
    experienceYears: "",
  });
  const [savingDriver, setSavingDriver] = useState(false);

  // Radar
  const [radarRadius, setRadarRadius] = useState(40);
  const [radarSweepActive, setRadarSweepActive] = useState(true);
  const [selectedRadarCab, setSelectedRadarCab] = useState(null);

  // Estimator
  const [estimatorDistance, setEstimatorDistance] = useState(18);
  const [estimatorCabType, setEstimatorCabType] = useState("Sedan");

  const authConfig = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const showNotice = useCallback((type, text) => {
    setNotice({ type, text });
  }, []);

  // auto hide notice
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  /* ---------- FETCH ---------- */
  const fetchCabs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, authConfig);
      const list = Array.isArray(res.data)
        ? res.data
        : res.data?.data || res.data?.cabs || [];
      setCabs(list);
    } catch (error) {
      console.error("Error fetching cabs:", error);
      showNotice("error", errMessage(error, "Could not load your cabs."));
    } finally {
      setLoading(false);
    }
  }, [authConfig, showNotice]);

  useEffect(() => {
    fetchCabs();
  }, [fetchCabs]);

  /* ---------- FILTER + SORT ---------- */
  const filteredCabs = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    const result = cabs.filter((cab) => {
      const driver = getDriver(cab).name.toLowerCase();
      const vNum = (cab.vehicleNumber || "").toLowerCase();
      const city = (cab.fromCity || cab.baseLocation || "").toLowerCase();
      const type = (cab.cabType || "").toLowerCase();
      const brand = (cab.brand || "").toLowerCase();
      const model = (cab.model || "").toLowerCase();

      const matchesQuery =
        !q ||
        vNum.includes(q) ||
        driver.includes(q) ||
        city.includes(q) ||
        type.includes(q) ||
        brand.includes(q) ||
        model.includes(q);

      if (!matchesQuery) return false;

      if (filterType === "available") return isCabLive(cab);
      if (filterType === "40km") return (cab.serviceRadiusKm || 40) >= 40;
      if (filterType === "driver") return Boolean(getDriver(cab).name);
      if (filterType === "sedan") return type.includes("sedan");
      if (filterType === "suv") return type.includes("suv");
      if (filterType === "mini") return type.includes("mini");
      if (filterType.startsWith("type:")) {
        return type === filterType.slice(5).toLowerCase();
      }
      return true;
    });

    if (sortBy === "price_asc") {
      result.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    } else if (sortBy === "price_desc") {
      result.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
    } else if (sortBy === "seats") {
      result.sort(
        (a, b) => (Number(b.availableSeats) || 0) - (Number(a.availableSeats) || 0)
      );
    }

    return result;
  }, [cabs, searchQuery, filterType, sortBy]);

  // pagination (applies to both table and card views)
  const totalPages = Math.max(1, Math.ceil(filteredCabs.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pagedCabs = filteredCabs.slice(pageStart, pageStart + PAGE_SIZE);

  const filterLabel = filterType.startsWith("type:")
    ? filterType.slice(5)
    : filterType;

  const applyFilter = (value) => {
    setFilterType(value);
    setPage(1);
  };

  const handleKpiClick = (type) => {
    applyFilter(filterType === type ? "all" : type);
  };

  /* ---------- FORM HELPERS ---------- */
  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  const resetForm = () => {
    setForm(makeInitialForm());
    setErrors({});
    setImage(null);
    setImagePreview(null);
    setExistingGallery([]);
    setNewGallery([]);
    setEditId(null);
    setFormTab("vehicle");
  };

  const openAdd = () => {
    resetForm();
    setSelectedCab(null);
    setShowForm(true);
    window.scrollTo({ top: 0 });
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
    window.scrollTo({ top: 0 });
  };

  const editCab = (cab) => {
    const d = getDriver(cab);
    const hasValue = (v) => v !== undefined && v !== null && v !== "";

    setForm({
      cabType: cab.cabType || "Sedan",
      vehicleNumber: cab.vehicleNumber || "",
      brand: cab.brand || "",
      model: cab.model || "",
      modelYear: hasValue(cab.modelYear) ? String(cab.modelYear) : "",
      vehicleColor: cab.vehicleColor || "",
      totalSeats: hasValue(cab.totalSeats) ? String(cab.totalSeats) : "4",
      availableSeats: hasValue(cab.availableSeats) ? String(cab.availableSeats) : "4",
      acType: cab.acType || "AC",
      fuelType: cab.fuelType || "Petrol",

      driverName: d.name,
      driverPhone: d.phone,
      driverAlternatePhone: d.alternatePhone,
      driverLicenseNumber: d.license,
      driverExperience: hasValue(d.exp) ? String(d.exp) : "",

      baseLocation: cab.baseLocation || cab.pickupLocation || "",
      pickupLocation: cab.pickupLocation || cab.baseLocation || "",
      fromCity: cab.fromCity || "",
      toCity: cab.toCity || "",
      serviceRadiusKm: hasValue(cab.serviceRadiusKm) ? String(cab.serviceRadiusKm) : "40",
      operatingArea: cab.operatingArea || "",

      price: hasValue(cab.price) ? String(cab.price) : "",
      pricePerKm: hasValue(cab.pricePerKm) ? String(cab.pricePerKm) : "14",
      pickupTime: cab.pickupTime || "09:00",
    });

    setErrors({});
    setImage(null);
    setImagePreview(cab.image || null);
    setExistingGallery(Array.isArray(cab.gallery) ? cab.gallery : []);
    setNewGallery([]);
    setEditId(cab._id);
    setFormTab("vehicle");
    setSelectedCab(null);
    setSelectedRadarCab(null);
    setActiveMainTab("fleet");
    setShowForm(true);
    window.scrollTo({ top: 0 });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleGalleryAdd = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    const room = MAX_GALLERY - existingGallery.length - newGallery.length;
    if (files.length > room) {
      showNotice("error", `Maximum ${MAX_GALLERY} gallery images allowed.`);
    }
    const accepted = files
      .slice(0, Math.max(room, 0))
      .map((file) => ({ file, url: URL.createObjectURL(file) }));
    setNewGallery((prev) => [...prev, ...accepted]);
  };

  const removeNewGallery = (index) =>
    setNewGallery((prev) => prev.filter((_, i) => i !== index));

  const removeExistingGallery = (url) =>
    setExistingGallery((prev) => prev.filter((u) => u !== url));

  /* ---------- VALIDATION ---------- */
  const validate = () => {
    const err = {};
    const price = form.price === "" ? NaN : Number(form.price);
    const perKm = form.pricePerKm === "" ? NaN : Number(form.pricePerKm);
    const total = Number(form.totalSeats);
    const avail = Number(form.availableSeats);

    if (!form.cabType) err.cabType = "Select a cab category";
    if (!form.vehicleNumber.trim()) err.vehicleNumber = "Vehicle plate number is required";

    if (!Number.isFinite(total) || total < 1) err.totalSeats = "Enter at least 1 seat";
    if (!Number.isFinite(avail) || avail < 0) err.availableSeats = "Cannot be negative";
    else if (Number.isFinite(total) && avail > total) {
      err.availableSeats = "Cannot be more than total seats";
    }
    if (form.modelYear && (Number(form.modelYear) < 1990 || Number(form.modelYear) > new Date().getFullYear() + 1)) {
      err.modelYear = "Enter a valid year";
    }

    if (!form.driverName.trim()) err.driverName = "Driver name is required";
    const phoneDigits = digitsOnly(form.driverPhone).length;
    if (!form.driverPhone.trim()) err.driverPhone = "Driver mobile number is required";
    else if (phoneDigits < 10 || phoneDigits > 13) err.driverPhone = "Enter a valid mobile number";
    if (!form.driverLicenseNumber.trim()) err.driverLicenseNumber = "Licence number is required";
    if (form.driverExperience !== "" && Number(form.driverExperience) < 0) {
      err.driverExperience = "Cannot be negative";
    }

    if (!form.baseLocation.trim()) err.baseLocation = "Base hub location is required";
    if (!form.fromCity.trim()) err.fromCity = "Operating city is required";

    if (!Number.isFinite(price) || price < 0) err.price = "Enter a valid base fare";
    if (!Number.isFinite(perKm) || perKm < 0) err.pricePerKm = "Enter a valid per km rate";

    setErrors(err);
    return err;
  };

  const goToFirstError = (err) => {
    const firstKey = Object.keys(err)[0];
    if (!firstKey) return;
    const tab = FIELD_TAB[firstKey] || "vehicle";
    setFormTab(tab);
    setTimeout(() => {
      document
        .querySelector(".vc-has-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  /* ---------- SUBMIT (ADD / EDIT) ---------- */
  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const err = validate();
    if (Object.keys(err).length) {
      goToFirstError(err);
      showNotice("error", "Please fix the highlighted fields before saving.");
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, typeof value === "string" ? value.trim() : value);
      }
    });

    // price engine mapping
    formData.set("baseFare", form.price || "0");
    formData.set("price", form.price || "0");

    // location mapping
    formData.set("baseLocation", form.baseLocation.trim());
    formData.set("pickupLocation", (form.pickupLocation || form.baseLocation).trim());
    formData.set("serviceRadiusKm", form.serviceRadiusKm || "40");

    // structured driver payload in addition to flattened fields
    formData.set(
      "driver",
      JSON.stringify({
        name: form.driverName.trim(),
        phone: form.driverPhone.trim(),
        alternatePhone: form.driverAlternatePhone.trim(),
        licenseNumber: form.driverLicenseNumber.trim(),
        experienceYears: Number(form.driverExperience) || 0,
      })
    );

    // gallery: urls to keep + new files
    formData.set("existingGallery", JSON.stringify(existingGallery));
    newGallery.forEach((g) => formData.append("gallery", g.file));
    if (image) formData.append("image", image);

    setSaving(true);
    try {
      // NOTE: do not set Content-Type manually; the browser adds the multipart boundary.
      if (editId) {
        await axios.put(`${API}/${editId}`, formData, authConfig);
      } else {
        await axios.post(API, formData, authConfig);
      }

      const wasEdit = Boolean(editId);
      setShowForm(false);
      resetForm();
      await fetchCabs();
      showNotice("success", wasEdit ? "Cab updated successfully." : "Cab added to your fleet.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error saving cab:", error);
      showNotice("error", errMessage(error, "Failed to save cab."));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  /* ---------- QUICK ACTIONS ---------- */
  const toggleStatus = async (id) => {
    setCabs((prev) =>
      prev.map((c) => (c._id === id ? { ...c, isActive: c.isActive === false } : c))
    );
    try {
      await axios.patch(`${API}/${id}/toggle-status`, {}, authConfig);
    } catch (error) {
      console.error("Error toggling status:", error);
      showNotice("error", errMessage(error, "Could not change status."));
      fetchCabs();
    }
  };

  const adjustSeats = async (cab, delta, e) => {
    e?.stopPropagation();
    const current = cab.availableSeats !== undefined && cab.availableSeats !== null
      ? Number(cab.availableSeats)
      : 4;
    const max = Number(cab.totalSeats) || 4;
    const newSeats = Math.max(0, Math.min(max, current + delta));
    if (newSeats === current) return;

    setCabs((prev) =>
      prev.map((c) => (c._id === cab._id ? { ...c, availableSeats: newSeats } : c))
    );
    try {
      await axios.put(`${API}/${cab._id}`, { availableSeats: newSeats }, authConfig);
    } catch (error) {
      console.error("Failed to update seats:", error);
      showNotice("error", errMessage(error, "Could not update seats."));
      fetchCabs();
    }
  };

  const openDriverModal = (cab, e) => {
    e?.stopPropagation();
    const d = getDriver(cab);
    setDriverModalCab(cab);
    setDriverForm({
      name: d.name,
      phone: d.phone,
      alternatePhone: d.alternatePhone,
      licenseNumber: d.license,
      experienceYears: d.exp ? String(d.exp) : "",
    });
  };

  const handleSaveDriver = async (e) => {
    e.preventDefault();
    if (!driverModalCab || savingDriver) return;

    const phoneDigits = digitsOnly(driverForm.phone).length;
    if (phoneDigits < 10 || phoneDigits > 13) {
      showNotice("error", "Enter a valid driver mobile number.");
      return;
    }

    setSavingDriver(true);
    const exp = Number(driverForm.experienceYears) || 0;
    const payload = {
      driver: {
        name: driverForm.name.trim(),
        phone: driverForm.phone.trim(),
        alternatePhone: driverForm.alternatePhone.trim(),
        licenseNumber: driverForm.licenseNumber.trim(),
        experienceYears: exp,
      },
      driverName: driverForm.name.trim(),
      driverPhone: driverForm.phone.trim(),
      driverAlternatePhone: driverForm.alternatePhone.trim(),
      driverLicenseNumber: driverForm.licenseNumber.trim(),
      driverExperience: exp,
    };

    try {
      await axios.put(`${API}/${driverModalCab._id}`, payload, authConfig);
      setCabs((prev) =>
        prev.map((c) =>
          c._id === driverModalCab._id
            ? { ...c, ...payload, driver: { ...(c.driver || {}), ...payload.driver } }
            : c
        )
      );
      setDriverModalCab(null);
      showNotice("success", "Driver details saved.");
    } catch (error) {
      console.error("Failed to update driver:", error);
      showNotice("error", errMessage(error, "Failed to update driver details."));
    } finally {
      setSavingDriver(false);
    }
  };

  const deleteCab = async (cab) => {
    if (!window.confirm(`Delete cab ${cab.vehicleNumber || ""}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/${cab._id}`, authConfig);
      setCabs((prev) => prev.filter((c) => c._id !== cab._id));
      if (selectedCab?._id === cab._id) setSelectedCab(null);
      if (selectedRadarCab?._id === cab._id) setSelectedRadarCab(null);
      showNotice("success", "Cab deleted.");
    } catch (error) {
      console.error("Error deleting cab:", error);
      showNotice("error", errMessage(error, "Failed to delete cab."));
    }
  };

  /* ---------- RADAR + ESTIMATOR DATA ---------- */
  const radarCabsWithCoords = useMemo(() => {
    return cabs.map((cab, index) => {
      const seed = cab._id
        ? cab._id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
        : index * 47;
      const angle = (seed * 53) % 360;
      const distanceRatio = (((seed * 29) % 85) + 10) / 100; // 0.1 to 0.95
      const distanceKm = Math.round(distanceRatio * 50);
      const inZone = distanceKm <= radarRadius;

      const rad = (angle * Math.PI) / 180;
      const r = distanceRatio * 42;
      const x = 50 + r * Math.cos(rad);
      const y = 50 + r * Math.sin(rad);

      return { ...cab, distanceKm, inZone, x, y, angle };
    });
  }, [cabs, radarRadius]);

  const estimatedFare = useMemo(() => {
    const base = BASE_RATES[estimatorCabType] || 350;
    const perKm = PER_KM_RATES[estimatorCabType] || 15;
    const total = base + estimatorDistance * perKm;
    const etaMins = Math.max(8, Math.round(estimatorDistance * 2.2));
    return { base, perKm, total, etaMins };
  }, [estimatorDistance, estimatorCabType]);

  /* ---------- SMALL RENDER HELPERS ---------- */
  const renderStatusToggle = (cab, liveText, offText) => (
    <div
      className="vc-card-toggle-wrap"
      onClick={() => toggleStatus(cab._id)}
      title="Click to toggle active availability"
      role="switch"
      aria-checked={cab.isActive !== false}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleStatus(cab._id);
        }
      }}
    >
      <div className={`vc-toggle-switch ${cab.isActive !== false ? "on" : ""}`}>
        <div className="vc-toggle-knob" />
      </div>
      <span className="vc-toggle-label">
        {cab.isActive !== false ? liveText : offText}
      </span>
    </div>
  );

  const renderRowActions = (cab) => (
    <div className="vc-card-actions">
      <button
        type="button"
        className="vc-btn-icon"
        onClick={() => setSelectedCab(cab)}
        title="Quick view"
      >
        <MdVisibility />
      </button>
      <button
        type="button"
        className="vc-btn-icon"
        onClick={() => editCab(cab)}
        title="Edit vehicle & driver"
      >
        <MdEdit />
      </button>
      <button
        type="button"
        className="vc-btn-icon delete"
        onClick={() => deleteCab(cab)}
        title="Delete cab"
      >
        <MdDelete />
      </button>
    </div>
  );

  const renderPagination = () => {
    if (filteredCabs.length <= PAGE_SIZE) return null;
    const from = pageStart + 1;
    const to = Math.min(pageStart + PAGE_SIZE, filteredCabs.length);
    return (
      <div className="vc-pagination">
        <span>
          Showing {from}–{to} of {filteredCabs.length} cabs
        </span>
        <div className="vc-pagination-btns">
          <button
            type="button"
            className="vc-btn-cancel"
            disabled={safePage <= 1}
            onClick={() => setPage(safePage - 1)}
          >
            ‹ Prev
          </button>
          <span className="vc-page-indicator">
            Page {safePage} of {totalPages}
          </span>
          <button
            type="button"
            className="vc-btn-cancel"
            disabled={safePage >= totalPages}
            onClick={() => setPage(safePage + 1)}
          >
            Next ›
          </button>
        </div>
      </div>
    );
  };

  const noticeBar = notice && (
    <div className={`vc-notice ${notice.type}`} role="status">
      <span>{notice.text}</span>
      <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );

  /* =========================================================
     FULL PAGE ADD / EDIT FORM
  ========================================================= */
  if (showForm) {
    const galleryCount = existingGallery.length + newGallery.length;
    const tabIndex = FORM_TABS.findIndex((t) => t.id === formTab);

    return (
      <div className="vc-container">
        {noticeBar}

        <div className="vc-form-page">
          <div className="vc-form-page-head">
            <h2>
              <MdDirectionsCar style={{ color: "#2563eb" }} />
              {editId ? "Edit Cab, Driver & 40km Hub" : "Add Cab to Fleet Inventory"}
            </h2>
            <button
              type="button"
              className="vc-btn-cancel"
              onClick={closeForm}
              disabled={saving}
            >
              ‹ Back to list
            </button>
          </div>

          <div className="vc-modal-tabs">
            {FORM_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`vc-modal-tab-btn ${formTab === t.id ? "active" : ""}`}
                onClick={() => setFormTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} noValidate>
            <div className="vc-form-page-body">
              {/* TAB 1: VEHICLE */}
              {formTab === "vehicle" && (
                <div>
                  <div className="vc-form-row">
                    <Field label="Cab Category *" error={errors.cabType}>
                      <select
                        className="vc-select"
                        value={form.cabType}
                        onChange={(e) => update("cabType", e.target.value)}
                      >
                        {CAB_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {CAB_TYPE_ICONS[t]} {t}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Vehicle Plate Number *" error={errors.vehicleNumber}>
                      <input
                        className="vc-input"
                        placeholder="e.g. DL 01 AB 1234"
                        value={form.vehicleNumber}
                        onChange={(e) => update("vehicleNumber", e.target.value.toUpperCase())}
                      />
                    </Field>
                  </div>

                  <div className="vc-form-row">
                    <Field label="Brand / Make">
                      <input
                        className="vc-input"
                        placeholder="e.g. Maruti Suzuki, Toyota, Hyundai"
                        value={form.brand}
                        onChange={(e) => update("brand", e.target.value)}
                      />
                    </Field>
                    <Field label="Model Name">
                      <input
                        className="vc-input"
                        placeholder="e.g. Dzire, Ertiga, Innova Crysta"
                        value={form.model}
                        onChange={(e) => update("model", e.target.value)}
                      />
                    </Field>
                  </div>

                  <div className="vc-form-row">
                    <Field label="Manufacturing Year" error={errors.modelYear}>
                      <input
                        className="vc-input"
                        type="number"
                        placeholder="e.g. 2023"
                        value={form.modelYear}
                        onChange={(e) => update("modelYear", e.target.value)}
                      />
                    </Field>
                    <Field label="Vehicle Color">
                      <input
                        className="vc-input"
                        placeholder="e.g. White, Silver, Black"
                        value={form.vehicleColor}
                        onChange={(e) => update("vehicleColor", e.target.value)}
                      />
                    </Field>
                  </div>

                  <div className="vc-form-row">
                    <Field label="Total Seats" error={errors.totalSeats}>
                      <input
                        className="vc-input"
                        type="number"
                        min="1"
                        value={form.totalSeats}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({ ...prev, totalSeats: v, availableSeats: v }));
                          setErrors((prev) => ({ ...prev, totalSeats: "", availableSeats: "" }));
                        }}
                      />
                    </Field>
                    <Field label="Available Seats (Live)" error={errors.availableSeats}>
                      <input
                        className="vc-input"
                        type="number"
                        min="0"
                        value={form.availableSeats}
                        onChange={(e) => update("availableSeats", e.target.value)}
                      />
                    </Field>
                  </div>

                  <div className="vc-form-row">
                    <Field label="AC Condition">
                      <select
                        className="vc-select"
                        value={form.acType}
                        onChange={(e) => update("acType", e.target.value)}
                      >
                        <option value="AC">AC</option>
                        <option value="Non-AC">Non-AC</option>
                      </select>
                    </Field>
                    <Field label="Fuel Type">
                      <select
                        className="vc-select"
                        value={form.fuelType}
                        onChange={(e) => update("fuelType", e.target.value)}
                      >
                        <option value="Petrol">Petrol</option>
                        <option value="Diesel">Diesel</option>
                        <option value="CNG">CNG</option>
                        <option value="Electric">Electric</option>
                        <option value="Hybrid">Hybrid</option>
                      </select>
                    </Field>
                  </div>
                </div>
              )}

              {/* TAB 2: DRIVER */}
              {formTab === "driver" && (
                <div>
                  <Field label="Driver Full Name *" error={errors.driverName}>
                    <input
                      className="vc-input"
                      placeholder="e.g. Ramesh Kumar"
                      value={form.driverName}
                      onChange={(e) => update("driverName", e.target.value)}
                    />
                  </Field>

                  <div className="vc-form-row">
                    <Field label="Driver Mobile Number *" error={errors.driverPhone}>
                      <input
                        className="vc-input"
                        type="tel"
                        placeholder="e.g. +91 9876543210"
                        value={form.driverPhone}
                        onChange={(e) => update("driverPhone", e.target.value)}
                      />
                    </Field>
                    <Field label="Alternate Contact">
                      <input
                        className="vc-input"
                        type="tel"
                        placeholder="e.g. +91 9123456780"
                        value={form.driverAlternatePhone}
                        onChange={(e) => update("driverAlternatePhone", e.target.value)}
                      />
                    </Field>
                  </div>

                  <div className="vc-form-row">
                    <Field label="Driving Licence Number *" error={errors.driverLicenseNumber}>
                      <input
                        className="vc-input"
                        placeholder="e.g. DL-1420110012345"
                        value={form.driverLicenseNumber}
                        onChange={(e) => update("driverLicenseNumber", e.target.value.toUpperCase())}
                      />
                    </Field>
                    <Field label="Experience (Years)" error={errors.driverExperience}>
                      <input
                        className="vc-input"
                        type="number"
                        min="0"
                        placeholder="e.g. 5"
                        value={form.driverExperience}
                        onChange={(e) => update("driverExperience", e.target.value)}
                      />
                    </Field>
                  </div>
                </div>
              )}

              {/* TAB 3: LOCATION */}
              {formTab === "location" && (
                <div>
                  <div className="vc-form-row">
                    <Field label="Base Hub Location *" error={errors.baseLocation}>
                      <input
                        className="vc-input"
                        placeholder="e.g. IGI Airport Terminal 3 / Connaught Place"
                        value={form.baseLocation}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({ ...prev, baseLocation: v, pickupLocation: v }));
                          setErrors((prev) => ({ ...prev, baseLocation: "" }));
                        }}
                      />
                    </Field>
                    <Field label="Operating City *" error={errors.fromCity}>
                      <input
                        className="vc-input"
                        placeholder="e.g. Delhi NCR, Jaipur, Mumbai"
                        value={form.fromCity}
                        onChange={(e) => update("fromCity", e.target.value)}
                      />
                    </Field>
                  </div>

                  <Field label="Drop / Destination City (optional)">
                    <input
                      className="vc-input"
                      placeholder="e.g. Agra"
                      value={form.toCity}
                      onChange={(e) => update("toCity", e.target.value)}
                    />
                  </Field>

                  <div className="vc-form-group">
                    <div className="vc-radius-row">
                      <label>Service Operating Radius:</label>
                      <span className="vc-location-badge-40km">
                        🎯 Within {form.serviceRadiusKm} km
                      </span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="60"
                      step="5"
                      value={form.serviceRadiusKm}
                      onChange={(e) => update("serviceRadiusKm", e.target.value)}
                      className="vc-range-slider"
                    />
                    <small className="vc-help-text">
                      Standard BMT intra-city operations are optimized for <strong>40 km</strong> local pickups.
                    </small>
                  </div>

                  <Field label="Preferred Operating Areas / Corridors">
                    <input
                      className="vc-input"
                      placeholder="e.g. South Delhi, Noida Sector 62, Gurgaon Cyber City"
                      value={form.operatingArea}
                      onChange={(e) => update("operatingArea", e.target.value)}
                    />
                  </Field>
                </div>
              )}

              {/* TAB 4: PRICING + PHOTOS */}
              {formTab === "pricing" && (
                <div>
                  <div className="vc-form-row">
                    <Field label="Base Trip Fare (₹) *" error={errors.price}>
                      <input
                        className="vc-input"
                        type="number"
                        min="0"
                        placeholder="e.g. 500"
                        value={form.price}
                        onChange={(e) => update("price", e.target.value)}
                      />
                    </Field>
                    <Field label="Per KM Rate (₹) *" error={errors.pricePerKm}>
                      <input
                        className="vc-input"
                        type="number"
                        min="0"
                        placeholder="e.g. 14"
                        value={form.pricePerKm}
                        onChange={(e) => update("pricePerKm", e.target.value)}
                      />
                    </Field>
                  </div>

                  <div className="vc-form-row">
                    <Field label="Default Pickup Time">
                      <input
                        className="vc-input"
                        type="time"
                        value={form.pickupTime}
                        onChange={(e) => update("pickupTime", e.target.value)}
                      />
                    </Field>
                    <span />
                  </div>

                  <div className="vc-form-group">
                    <label>Primary Vehicle Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="vc-input vc-file-input"
                    />
                    {imagePreview && (
                      <div className="vc-photo-preview">
                        <img src={imagePreview} alt="Preview" />
                      </div>
                    )}
                  </div>

                  <div className="vc-form-group">
                    <label>
                      Gallery Photos ({galleryCount}/{MAX_GALLERY})
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleGalleryAdd}
                      className="vc-input vc-file-input"
                      disabled={galleryCount >= MAX_GALLERY}
                    />
                    {galleryCount > 0 && (
                      <div className="vc-gallery-grid">
                        {existingGallery.map((url) => (
                          <div className="vc-gallery-item" key={url}>
                            <img src={url} alt="Saved gallery" />
                            <button type="button" onClick={() => removeExistingGallery(url)}>
                              Remove
                            </button>
                          </div>
                        ))}
                        {newGallery.map((g, i) => (
                          <div className="vc-gallery-item" key={g.url}>
                            <img src={g.url} alt={g.file.name} />
                            <button type="button" onClick={() => removeNewGallery(i)}>
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="vc-wizard-nav-btns">
                {tabIndex > 0 ? (
                  <button
                    type="button"
                    className="vc-btn-cancel"
                    onClick={() => setFormTab(FORM_TABS[tabIndex - 1].id)}
                  >
                    ‹ Back
                  </button>
                ) : (
                  <button
                    type="button"
                    className="vc-btn-cancel"
                    onClick={closeForm}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                )}

                {tabIndex < FORM_TABS.length - 1 ? (
                  <button
                    type="button"
                    className="vc-btn-submit"
                    onClick={() => setFormTab(FORM_TABS[tabIndex + 1].id)}
                  >
                    Next: {FORM_TABS[tabIndex + 1].label.replace(/^\S+\s\d\.\s/, "")} ➔
                  </button>
                ) : (
                  <button type="submit" className="vc-btn-submit" disabled={saving}>
                    {saving ? "Saving Cab..." : editId ? "Update Cab" : "✓ Register Cab"}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  /* =========================================================
     MAIN DASHBOARD
  ========================================================= */
  return (
    <div className="vc-container">
      {/* HEADER */}
      <header className="vc-header">
        <div className="vc-title-wrap">
          <h1>
            <span className="vc-title-emoji">🚕</span>
            Cab Fleet & Dispatch Management
            <span className="vc-live-pill">
              <span className="vc-live-dot" /> Live Dispatch Engine
            </span>
          </h1>
          <p>
            Real-time fleet operations, verified driver dispatching, and high-precision 40 km coverage radius.
          </p>
        </div>

        <div className="vc-header-actions">
          <div className="vc-mode-pill-group">
            <button
              type="button"
              className={`vc-mode-pill ${activeMainTab === "fleet" ? "active" : ""}`}
              onClick={() => setActiveMainTab("fleet")}
            >
              <MdGridView /> Fleet Manager
            </button>
            <button
              type="button"
              className={`vc-mode-pill ${activeMainTab === "radar" ? "active" : ""}`}
              onClick={() => setActiveMainTab("radar")}
            >
              <MdRadar /> 40 KM Radar Live
              <span className="vc-mini-badge">{cabs.length}</span>
            </button>
            <button
              type="button"
              className={`vc-mode-pill ${activeMainTab === "estimator" ? "active" : ""}`}
              onClick={() => setActiveMainTab("estimator")}
            >
              <MdCalculate /> Fare & ETA Calc
            </button>
          </div>

          <button
            type="button"
            className="vc-btn-refresh"
            onClick={fetchCabs}
            title="Refresh fleet"
            aria-label="Refresh fleet"
          >
            <MdRefresh />
          </button>

          <button type="button" className="vc-btn-add" onClick={openAdd}>
            <MdAdd style={{ fontSize: "20px" }} />
            Add New Cab
          </button>
        </div>
      </header>

      {noticeBar}

      {/* KPI ROW */}
      <section className="vc-stats-grid">
        <div
          className={`vc-stat-card ${filterType === "all" ? "vc-kpi-active" : ""}`}
          onClick={() => handleKpiClick("all")}
          title="Click to view all fleet vehicles"
        >
          <div className="vc-stat-info">
            <small>Total Fleet Cabs</small>
            <strong>{cabs.length}</strong>
            <span className="vc-kpi-hint">
              {filterType === "all" ? "✓ Filter Active" : "Click to view all"}
            </span>
          </div>
          <div className="vc-stat-icon-wrap vc-stat-icon-blue">
            <MdDirectionsCar />
          </div>
        </div>

        <div
          className={`vc-stat-card ${filterType === "available" ? "vc-kpi-active" : ""}`}
          onClick={() => handleKpiClick("available")}
          title="Click to filter live available cabs"
        >
          <div className="vc-stat-info">
            <small>Live & Available</small>
            <strong style={{ color: "#10b981" }}>{cabs.filter(isCabLive).length}</strong>
            <span className="vc-kpi-hint">
              {filterType === "available" ? "✓ Filter Active" : "Click to filter live"}
            </span>
          </div>
          <div className="vc-stat-icon-wrap vc-stat-icon-green">
            <MdCheckCircle />
          </div>
        </div>

        <div
          className={`vc-stat-card ${filterType === "40km" ? "vc-kpi-active" : ""}`}
          onClick={() => handleKpiClick("40km")}
          title="Click to filter vehicles configured for 40km hub"
        >
          <div className="vc-stat-info">
            <small>40 KM Hub Active</small>
            <strong style={{ color: "#2563eb" }}>
              {cabs.filter((c) => (c.serviceRadiusKm || 40) >= 40).length} Cabs
            </strong>
            <span className="vc-kpi-hint">
              {filterType === "40km" ? "✓ Filter Active" : "Click to filter 40km"}
            </span>
          </div>
          <div className="vc-stat-icon-wrap vc-stat-icon-amber">
            <MdRadar />
          </div>
        </div>

        <div
          className={`vc-stat-card ${filterType === "driver" ? "vc-kpi-active" : ""}`}
          onClick={() => handleKpiClick("driver")}
          title="Click to filter cabs with assigned drivers"
        >
          <div className="vc-stat-info">
            <small>Drivers Assigned</small>
            <strong style={{ color: "#8b5cf6" }}>
              {cabs.filter((c) => getDriver(c).name).length}
            </strong>
            <span className="vc-kpi-hint">
              {filterType === "driver" ? "✓ Filter Active" : "Click to filter drivers"}
            </span>
          </div>
          <div className="vc-stat-icon-wrap vc-stat-icon-purple">
            <MdPerson />
          </div>
        </div>
      </section>

      {/* =========================================================
          VIEW 1: RADAR
      ========================================================= */}
      {activeMainTab === "radar" && (
        <section className="vc-radar-interactive-section">
          <div className="vc-radar-banner">
            <div className="vc-radar-banner-left">
              <h2>
                <MdRadar style={{ color: "#38bdf8" }} />
                Real-Time 40 KM Local Coverage Radar
              </h2>
              <p>
                Interactive GPS telematics visualizer for your fleet. Vehicles within the {radarRadius} km zone receive instant passenger bookings with optimal arrival times.
              </p>
            </div>

            <div className="vc-radar-controls">
              <div className="vc-radar-slider-wrap">
                <label>
                  <span>Dynamic Service Radius:</span>
                  <strong className="vc-radius-highlight">{radarRadius} KM</strong>
                </label>
                <input
                  type="range"
                  min="10"
                  max="70"
                  step="5"
                  value={radarRadius}
                  onChange={(e) => setRadarRadius(Number(e.target.value))}
                  className="vc-range-slider"
                />
                <div className="vc-slider-ticks">
                  <span>10km</span>
                  <span style={{ color: "#38bdf8", fontWeight: "700" }}>40km (Standard)</span>
                  <span>70km</span>
                </div>
              </div>

              <button
                type="button"
                className={`vc-radar-toggle-btn ${radarSweepActive ? "active" : ""}`}
                onClick={() => setRadarSweepActive((v) => !v)}
              >
                {radarSweepActive ? <MdPause /> : <MdPlayArrow />}
                {radarSweepActive ? "Pause Radar" : "Start Sweep"}
              </button>
            </div>
          </div>

          <div className="vc-radar-viewport-grid">
            <div className="vc-radar-canvas-container">
              <div className="vc-radar-screen">
                <div className="vc-radar-ring vc-ring-10km" />
                <div className="vc-radar-ring vc-ring-20km" />
                <div className="vc-radar-ring vc-ring-30km" />
                <div
                  className="vc-radar-ring vc-ring-40km active-hub-ring"
                  style={{
                    boxShadow: radarRadius >= 40 ? "0 0 20px rgba(56, 189, 248, 0.4)" : "none",
                  }}
                />
                <div className="vc-radar-ring vc-ring-outer" />

                <div className="vc-radar-axis-x" />
                <div className="vc-radar-axis-y" />

                {radarSweepActive && <div className="vc-radar-sweep-beam" />}

                <div className="vc-radar-center-hub" title="Primary Fleet Dispatch Hub">
                  <div className="vc-hub-pulse" />
                  <span className="vc-hub-pin">🎯</span>
                  <small>HUB</small>
                </div>

                {radarCabsWithCoords.map((cab) => {
                  const isSelected = selectedRadarCab?._id === cab._id;
                  const live = isCabLive(cab);
                  return (
                    <div
                      key={cab._id}
                      className={`vc-radar-blip ${cab.inZone ? "in-zone" : "out-zone"} ${
                        live ? "live" : "offline"
                      } ${isSelected ? "selected" : ""}`}
                      style={{ left: `${cab.x}%`, top: `${cab.y}%` }}
                      onClick={() => setSelectedRadarCab(cab)}
                      title={`${cab.vehicleNumber || "Cab"} (${cab.cabType || ""}) - ${cab.distanceKm} km from hub`}
                    >
                      <div className="vc-blip-dot">
                        <span className="vc-blip-icon">{CAB_TYPE_ICONS[cab.cabType] || "🚗"}</span>
                      </div>
                      <span className="vc-blip-tag">{(cab.vehicleNumber || "").slice(-4)}</span>
                    </div>
                  );
                })}
              </div>

              <div className="vc-radar-legend">
                <div className="vc-legend-item">
                  <span className="vc-legend-dot live-in" />
                  <span>
                    Available in {radarRadius}km (
                    {radarCabsWithCoords.filter((c) => c.inZone && c.isActive !== false).length})
                  </span>
                </div>
                <div className="vc-legend-item">
                  <span className="vc-legend-dot out" />
                  <span>Outside Zone ({radarCabsWithCoords.filter((c) => !c.inZone).length})</span>
                </div>
                <div className="vc-legend-item">
                  <span className="vc-legend-dot off" />
                  <span>
                    Offline / Inactive ({radarCabsWithCoords.filter((c) => c.isActive === false).length})
                  </span>
                </div>
              </div>
            </div>

            <div className="vc-radar-hud-card">
              {selectedRadarCab ? (
                (() => {
                  const sel = selectedRadarCab;
                  const sd = getDriver(sel);
                  const waNumber = digitsOnly(sd.phone);
                  return (
                    <div className="vc-hud-content">
                      <div className="vc-hud-header">
                        <div>
                          <span className="vc-hud-badge">
                            {sel.inZone ? "🎯 INSIDE ZONE" : "⚠️ OUTSIDE ZONE"}
                          </span>
                          <h3>
                            {sel.brand || sel.cabType} {sel.model}
                          </h3>
                          <span className="vc-hud-plate">{sel.vehicleNumber}</span>
                        </div>
                        <button
                          type="button"
                          className="vc-hud-close"
                          onClick={() => setSelectedRadarCab(null)}
                          aria-label="Close"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="vc-hud-telemetry-grid">
                        <div className="vc-telemetry-box">
                          <small>Distance From Hub</small>
                          <strong style={{ color: sel.inZone ? "#10b981" : "#f59e0b" }}>
                            {sel.distanceKm} KM
                          </strong>
                        </div>
                        <div className="vc-telemetry-box">
                          <small>Est. Dispatch ETA</small>
                          <strong style={{ color: "#38bdf8" }}>
                            ~{Math.max(6, Math.round(sel.distanceKm * 2))} Mins
                          </strong>
                        </div>
                        <div className="vc-telemetry-box">
                          <small>Available Seats</small>
                          <strong>
                            {sel.availableSeats || 0} / {sel.totalSeats || 4}
                          </strong>
                        </div>
                        <div className="vc-telemetry-box">
                          <small>Base Fare</small>
                          <strong style={{ color: "#10b981" }}>₹{sel.price || 0}</strong>
                        </div>
                      </div>

                      <div className="vc-hud-driver-box">
                        <div className="vc-hud-driver-avatar">👨‍✈️</div>
                        <div className="vc-hud-driver-info">
                          <h4>{sd.name || "Driver Unassigned"}</h4>
                          <p>📞 {sd.phone || "No contact"}</p>
                          <small>Licence: {sd.license || "Pending"}</small>
                        </div>
                      </div>

                      <div className="vc-hud-actions">
                        {sd.phone && (
                          <a href={`tel:${sd.phone}`} className="vc-hud-btn-call">
                            <MdPhone /> Call Driver
                          </a>
                        )}
                        {waNumber && (
                          <a
                            href={`https://wa.me/${waNumber}?text=${encodeURIComponent(
                              `Hi ${sd.name || "Driver"}, you have an active dispatch query for Cab ${sel.vehicleNumber} within the 40km hub zone.`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="vc-hud-btn-whatsapp"
                          >
                            <MdSend /> WhatsApp Dispatch
                          </a>
                        )}
                        <button
                          type="button"
                          className="vc-hud-btn-edit"
                          onClick={() => editCab(sel)}
                        >
                          <MdEdit /> Edit Specs
                        </button>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="vc-hud-empty">
                  <div style={{ fontSize: "48px", marginBottom: "12px" }}>🛰️</div>
                  <h4>Interactive Radar Telematics</h4>
                  <p>
                    Click any cab dot on the radar grid to view its driver, distance from base hub, estimated pickup ETA, and one-click dispatch actions.
                  </p>
                  <div className="vc-hud-quick-stats">
                    <div className="vc-hud-stat-pill">
                      <span>Total In Radius:</span>
                      <strong>{radarCabsWithCoords.filter((c) => c.inZone).length} Cabs</strong>
                    </div>
                    <div className="vc-hud-stat-pill">
                      <span>Live Response ETA:</span>
                      <strong>&lt; 15 mins</strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* =========================================================
          VIEW 2: ESTIMATOR
      ========================================================= */}
      {activeMainTab === "estimator" && (
        <section className="vc-estimator-section">
          <div className="vc-estimator-card">
            <div className="vc-estimator-head">
              <h2>
                <MdCalculate style={{ color: "#2563eb", fontSize: "32px" }} />
                Interactive 40 KM Fare & Dispatch Simulator
              </h2>
              <p>
                Simulate trip pricing and passenger pickup ETAs for local intra-city and airport drops within the 40 km radius.
              </p>
            </div>

            <div className="vc-estimator-body">
              <div className="vc-estimator-inputs">
                <div className="vc-calc-group">
                  <div className="vc-calc-label-row">
                    <label>Ride Distance within Hub:</label>
                    <span className="vc-calc-val">{estimatorDistance} KM</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="40"
                    step="1"
                    value={estimatorDistance}
                    onChange={(e) => setEstimatorDistance(Number(e.target.value))}
                    className="vc-range-slider vc-range-light"
                  />
                  <div className="vc-slider-ticks">
                    <span>1 KM (Local)</span>
                    <span>20 KM (Airport)</span>
                    <span style={{ color: "#2563eb", fontWeight: "700" }}>40 KM (Hub Boundary)</span>
                  </div>
                </div>

                <div className="vc-calc-group">
                  <label className="vc-calc-title">Select Cab Category:</label>
                  <div className="vc-car-type-chips">
                    {["Mini", "Sedan", "SUV", "Premium Sedan", "Premium SUV", "Luxury", "Electric"].map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={`vc-type-chip ${estimatorCabType === type ? "active" : ""}`}
                        onClick={() => setEstimatorCabType(type)}
                      >
                        <span>{CAB_TYPE_ICONS[type] || "🚗"}</span>
                        <span>{type}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="vc-estimator-receipt">
                <div className="vc-receipt-header">
                  <span>Fare Calculation Breakdown</span>
                  <span className="vc-receipt-tag">🎯 Within 40 km zone</span>
                </div>

                <div className="vc-receipt-lines">
                  <div className="vc-receipt-row">
                    <span>Base Flagdown Fare:</span>
                    <strong>₹{estimatedFare.base}</strong>
                  </div>
                  <div className="vc-receipt-row">
                    <span>
                      Distance Fare ({estimatorDistance} km × ₹{estimatedFare.perKm}/km):
                    </span>
                    <strong>₹{estimatorDistance * estimatedFare.perKm}</strong>
                  </div>
                  <div className="vc-receipt-row">
                    <span>Estimated Driver Arrival Time:</span>
                    <strong style={{ color: "#10b981" }}>~{estimatedFare.etaMins} Mins</strong>
                  </div>
                  <div className="vc-receipt-row">
                    <span>Deadhead Toll / Return Risk:</span>
                    <strong style={{ color: "#2563eb" }}>0% (Inside 40km Hub)</strong>
                  </div>
                  <div className="vc-receipt-divider" />
                  <div className="vc-receipt-total">
                    <span>Total Est. Fare:</span>
                    <span className="vc-total-val">₹{estimatedFare.total}</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="vc-btn-receipt-action"
                  onClick={() => {
                    applyFilter(`type:${estimatorCabType}`);
                    setActiveMainTab("fleet");
                  }}
                >
                  View Available {estimatorCabType} Fleet (
                  {cabs.filter((c) => (c.cabType || "").toLowerCase() === estimatorCabType.toLowerCase()).length})
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* =========================================================
          VIEW 3: FLEET MANAGER (TABLE / CARDS)
      ========================================================= */}
      {activeMainTab === "fleet" && (
        <>
          <div className="vc-toolbar">
            <div className="vc-search-box">
              <MdSearch className="vc-search-icon" />
              <input
                type="text"
                placeholder="Search by vehicle plate, driver, city, brand or model..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="vc-search-clear"
                  onClick={() => {
                    setSearchQuery("");
                    setPage(1);
                  }}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="vc-filter-pills">
              {FILTER_PILLS.map((pill) => (
                <button
                  key={pill.id}
                  type="button"
                  className={`vc-filter-pill ${filterType === pill.id ? "active" : ""}`}
                  onClick={() => applyFilter(pill.id)}
                >
                  {pill.label}
                </button>
              ))}
            </div>

            <div className="vc-sort-wrap">
              <select
                className="vc-sort-select"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
              >
                <option value="default">Sort: Default</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="seats">Most Seats Available</option>
              </select>
            </div>

            <div className="vc-view-toggle">
              <button
                type="button"
                className={`vc-view-btn ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
                title="Table view"
                aria-label="Table view"
              >
                <MdViewList />
              </button>
              <button
                type="button"
                className={`vc-view-btn ${viewMode === "cards" ? "active" : ""}`}
                onClick={() => setViewMode("cards")}
                title="Card view"
                aria-label="Card view"
              >
                <MdGridView />
              </button>
            </div>
          </div>

          {(filterType !== "all" || searchQuery) && (
            <div className="vc-active-filter-bar">
              <span>
                Showing {filteredCabs.length} of {cabs.length} vehicles matching:
                {filterType !== "all" && <strong> [{filterLabel.toUpperCase()}]</strong>}
                {searchQuery && <strong> [Search: "{searchQuery}"]</strong>}
              </span>
              <button
                type="button"
                className="vc-btn-reset-filter"
                onClick={() => {
                  setFilterType("all");
                  setSearchQuery("");
                  setPage(1);
                }}
              >
                Clear Filters
              </button>
            </div>
          )}

          {loading && (
            <div className="vc-loading-state">
              <div className="vc-spinner" />
              <strong>Loading your fleet...</strong>
            </div>
          )}
{!loading && filteredCabs.length === 0 && (
            <div className="vc-empty-state">
              <div style={{ fontSize: "54px", marginBottom: "16px" }}>🚕</div>
              <h3>No Fleet Cabs Found</h3>
              <p>
                {searchQuery || filterType !== "all"
                  ? "No fleet vehicles match your current search or filter. Try clearing the filters."
                  : 'Use the "Add New Cab" button above to add your first cab, driver and 40 km hub.'}
              </p>
            </div>
          )}

          {/* TABLE VIEW */}
          {!loading && filteredCabs.length > 0 && viewMode === "table" && (
            <div className="vc-table-wrap">
              <table className="vc-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Vehicle</th>
                    <th>Driver</th>
                    <th>Location & Radius</th>
                    <th>Seats</th>
                    <th>Fare</th>
                    <th>Status</th>
                    <th className="vc-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedCabs.map((cab, i) => {
                    const d = getDriver(cab);
                    const baseLoc =
                      cab.baseLocation || cab.pickupLocation || cab.fromCity || "Base Hub";
                    const radiusKm = cab.serviceRadiusKm || 40;
                    const live = isCabLive(cab);

                    return (
                      <tr key={cab._id}>
                        <td>{pageStart + i + 1}</td>

                        <td>
                          <div className="vc-cell-vehicle">
                            <div className="vc-thumb">
                              {cab.image ? (
                                <img src={cab.image} alt={cab.vehicleNumber || "Cab"} />
                              ) : (
                                <span>{CAB_TYPE_ICONS[cab.cabType] || "🚗"}</span>
                              )}
                            </div>
                            <div>
                              <strong className="vc-cell-title">{cab.vehicleNumber}</strong>
                              <small className="vc-cell-sub">
                                {[cab.cabType, cab.brand, cab.model].filter(Boolean).join(" · ")}
                              </small>
                            </div>
                          </div>
                        </td>

                        <td>
                          {d.name ? (
                            <div>
                              <strong className="vc-cell-title">{d.name}</strong>
                              {d.phone && (
                                <a href={`tel:${d.phone}`} className="vc-cell-link">
                                  📞 {d.phone}
                                </a>
                              )}
                              {d.license && (
                                <small className="vc-cell-sub">Licence: {d.license}</small>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="vc-btn-assign-small"
                              onClick={(e) => openDriverModal(cab, e)}
                            >
                              + Assign Driver
                            </button>
                          )}
                        </td>

                        <td>
                          <strong className="vc-cell-title">📍 {baseLoc}</strong>
                          <span className="vc-location-badge-40km vc-badge-inline">
                            🎯 Within {radiusKm} km
                          </span>
                        </td>

                        <td>
                          <div className="vc-seat-cell">
                            <button
                              type="button"
                              className="vc-seat-step-btn"
                              onClick={(e) => adjustSeats(cab, -1, e)}
                              disabled={(cab.availableSeats || 0) <= 0}
                              aria-label="Decrease seats"
                            >
                              -
                            </button>
                            <strong>
                              {cab.availableSeats || 0} / {cab.totalSeats || 4}
                            </strong>
                            <button
                              type="button"
                              className="vc-seat-step-btn"
                              onClick={(e) => adjustSeats(cab, 1, e)}
                              disabled={(cab.availableSeats || 0) >= (cab.totalSeats || 4)}
                              aria-label="Increase seats"
                            >
                              +
                            </button>
                          </div>
                        </td>

                        <td>
                          <strong className="vc-cell-price">₹{cab.price || 0}</strong>
                          {cab.pricePerKm ? (
                            <small className="vc-cell-sub">₹{cab.pricePerKm}/km</small>
                          ) : null}
                        </td>

                        <td>
                          <span
                            className={`vc-badge-status vc-status-inline ${
                              cab.isActive === false || !live ? "vc-status-inactive" : "vc-status-active"
                            }`}
                          >
                            {cab.isActive === false
                              ? "🔴 Inactive"
                              : live
                              ? "🟢 Available"
                              : "🟡 Occupied"}
                          </span>
                          {renderStatusToggle(cab, "Live", "Off")}
                        </td>

                        <td>
                          <div className="vc-row-actions">{renderRowActions(cab)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* CARD VIEW */}
          {!loading && filteredCabs.length > 0 && viewMode === "cards" && (
            <div className="vc-cards-grid">
              {pagedCabs.map((cab) => {
                const d = getDriver(cab);
                const baseLoc =
                  cab.baseLocation || cab.pickupLocation || cab.fromCity || "Primary Hub";
                const radiusKm = cab.serviceRadiusKm || 40;
                const live = isCabLive(cab);
                const waNumber = digitsOnly(d.phone);

                return (
                  <article key={cab._id} className="vc-card">
                    <div className="vc-card-media">
                      {cab.image ? (
                        <img src={cab.image} alt={cab.cabType} className="vc-card-img" />
                      ) : (
                        <div className="vc-card-no-img">
                          <span style={{ fontSize: "48px" }}>
                            {CAB_TYPE_ICONS[cab.cabType] || "🚗"}
                          </span>
                        </div>
                      )}

                      <div className="vc-card-badges">
                        <span className="vc-badge-plate">{cab.vehicleNumber}</span>
                        <span
                          className={`vc-badge-status ${
                            cab.isActive === false || !live ? "vc-status-inactive" : "vc-status-active"
                          }`}
                        >
                          {cab.isActive === false
                            ? "🔴 Inactive"
                            : live
                            ? "🟢 Available"
                            : "🟡 Occupied"}
                        </span>
                      </div>
                    </div>

                    <div className="vc-card-body">
                      <div className="vc-card-title-row">
                        <div>
                          <h4 className="vc-card-car-name">
                            {cab.brand ? `${cab.brand} ${cab.model || ""}` : `${cab.cabType} Cab`}
                          </h4>
                          <span className="vc-card-car-meta">
                            {CAB_TYPE_ICONS[cab.cabType]} {cab.cabType} · {cab.vehicleColor || "Standard"}
                          </span>
                        </div>

                        <div className="vc-card-price-pill">
                          <span className="vc-card-price-amount">₹{cab.price || 0}</span>
                          <span className="vc-card-price-label">Base Fare</span>
                        </div>
                      </div>

                      <div className="vc-driver-box">
                        <div className="vc-driver-avatar">
                          {cab.driver?.photo ? (
                            <img
                              src={cab.driver.photo}
                              alt={d.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            "👨‍✈️"
                          )}
                        </div>
                        <div className="vc-driver-meta">
                          <div className="vc-driver-header-line">
                            <h5 className="vc-driver-name">
                              {d.name || "Driver Unassigned"}
                              {d.name && <MdVerified style={{ color: "#2563eb", fontSize: "14px" }} />}
                            </h5>
                            <button
                              type="button"
                              className="vc-btn-reassign"
                              onClick={(e) => openDriverModal(cab, e)}
                              title="Assign or update driver"
                            >
                              <MdAssignmentInd /> {d.name ? "Change" : "Assign"}
                            </button>
                          </div>

                          {d.phone ? (
                            <div className="vc-driver-contact-actions">
                              <a href={`tel:${d.phone}`} className="vc-driver-phone-chip" title="Call driver">
                                📞 {d.phone}
                              </a>
                              {waNumber && (
                                <a
                                  href={`https://wa.me/${waNumber}?text=${encodeURIComponent(
                                    `Hi ${d.name}, notification regarding cab ${cab.vehicleNumber} (${cab.cabType}).`
                                  )}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="vc-driver-wa-chip"
                                  title="Send WhatsApp message"
                                >
                                  💬 WhatsApp
                                </a>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: "11px", color: "#94a3b8" }}>No phone linked</span>
                          )}

                          {d.license && (
                            <span className="vc-driver-licence">
                              DL: {d.license} {d.exp ? `· ${d.exp} yrs exp` : ""}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="vc-location-box">
                        <div className="vc-location-head">
                          <span className="vc-location-hub">
                            <MdLocationOn style={{ color: "#2563eb", fontSize: "16px" }} />
                            {baseLoc}
                          </span>
                          <span className="vc-location-badge-40km">🎯 Within {radiusKm} km</span>
                        </div>
                        <p className="vc-location-route">
                          {cab.fromCity || "Base Hub"}
                          {cab.toCity ? ` ➔ ${cab.toCity}` : " · Intra-City & Drops"}
                        </p>
                      </div>

                      <div className="vc-specs-bar">
                        <div className="vc-seat-stepper-wrap" title="Adjust available passenger seats">
                          <MdAirlineSeatReclineNormal style={{ fontSize: "15px", color: "#2563eb" }} />
                          <span>Seats:</span>
                          <button
                            type="button"
                            className="vc-seat-step-btn"
                            onClick={(e) => adjustSeats(cab, -1, e)}
                            disabled={(cab.availableSeats || 0) <= 0}
                            aria-label="Decrease seats"
                          >
                            -
                          </button>
                          <strong className="vc-seat-number">
                            {cab.availableSeats || 0} / {cab.totalSeats || 4}
                          </strong>
                          <button
                            type="button"
                            className="vc-seat-step-btn"
                            onClick={(e) => adjustSeats(cab, 1, e)}
                            disabled={(cab.availableSeats || 0) >= (cab.totalSeats || 4)}
                            aria-label="Increase seats"
                          >
                            +
                          </button>
                        </div>

                        <span className="vc-spec-tag">
                          <MdAcUnit /> {cab.acType || "AC"}
                        </span>
                        <span className="vc-spec-tag">
                          <MdLocalGasStation /> {cab.fuelType || "Petrol"}
                        </span>
                      </div>

                      <div className="vc-card-footer">
                        {renderStatusToggle(cab, "Active", "Inactive")}
                        {renderRowActions(cab)}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {!loading && renderPagination()}
        </>
      )}

      {/* =========================================================
          MODAL: QUICK DRIVER ASSIGNMENT
      ========================================================= */}
      {driverModalCab && (
        <div className="vc-modal-overlay" onClick={() => setDriverModalCab(null)}>
          <div
            className="vc-modal"
            style={{ maxWidth: "520px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vc-modal-head">
              <h2>
                <MdPerson style={{ color: "#2563eb" }} />
                Assign Driver · {driverModalCab.vehicleNumber}
              </h2>
              <button
                type="button"
                className="vc-modal-close"
                onClick={() => setDriverModalCab(null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveDriver}>
              <div className="vc-modal-body">
                <p className="vc-help-text" style={{ marginTop: 0 }}>
                  Attach a dedicated commercial driver to this vehicle for automatic trip dispatching within the 40 km hub.
                </p>

                <div className="vc-form-group">
                  <label>Driver Full Name *</label>
                  <input
                    className="vc-input"
                    placeholder="e.g. Ramesh Kumar"
                    value={driverForm.name}
                    onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })}
                    required
                  />
                </div>

                <div className="vc-form-row">
                  <div className="vc-form-group">
                    <label>Driver Mobile Number *</label>
                    <input
                      className="vc-input"
                      type="tel"
                      placeholder="e.g. +91 9876543210"
                      value={driverForm.phone}
                      onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="vc-form-group">
                    <label>Alternate Contact</label>
                    <input
                      className="vc-input"
                      type="tel"
                      placeholder="Optional emergency number"
                      value={driverForm.alternatePhone}
                      onChange={(e) =>
                        setDriverForm({ ...driverForm, alternatePhone: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="vc-form-row">
                  <div className="vc-form-group">
                    <label>Driving Licence Number *</label>
                    <input
                      className="vc-input"
                      placeholder="e.g. DL-1420110012345"
                      value={driverForm.licenseNumber}
                      onChange={(e) =>
                        setDriverForm({
                          ...driverForm,
                          licenseNumber: e.target.value.toUpperCase(),
                        })
                      }
                      required
                    />
                  </div>
                  <div className="vc-form-group">
                    <label>Experience (Years)</label>
                    <input
                      className="vc-input"
                      type="number"
                      min="0"
                      placeholder="e.g. 5"
                      value={driverForm.experienceYears}
                      onChange={(e) =>
                        setDriverForm({ ...driverForm, experienceYears: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="vc-modal-footer">
                  <button
                    type="button"
                    className="vc-btn-cancel"
                    onClick={() => setDriverModalCab(null)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="vc-btn-submit" disabled={savingDriver}>
                    {savingDriver ? "Saving..." : "Save Driver Assignment"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================
          MODAL: QUICK VIEW
      ========================================================= */}
      {selectedCab && (() => {
        const sd = getDriver(selectedCab);
        return (
          <div className="vc-modal-overlay" onClick={() => setSelectedCab(null)}>
            <div
              className="vc-modal"
              style={{ maxWidth: "680px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="vc-modal-head">
                <h2>
                  <MdDirectionsCar style={{ color: "#2563eb" }} />
                  {selectedCab.vehicleNumber} Details
                </h2>
                <button
                  type="button"
                  className="vc-modal-close"
                  onClick={() => setSelectedCab(null)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className="vc-modal-body" style={{ display: "grid", gap: "20px" }}>
                {selectedCab.image && (
                  <div className="vc-dossier-image">
                    <img src={selectedCab.image} alt={selectedCab.vehicleNumber} />
                  </div>
                )}

                <div className="vc-driver-box">
                  <span style={{ fontSize: "32px" }}>👨‍✈️</span>
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: "15px", color: "#0f172a" }}>
                      {sd.name || "Driver Not Assigned"}
                    </strong>
                    <div style={{ color: "#2563eb", fontWeight: "600", fontSize: "13px" }}>
                      📞 {sd.phone || "N/A"}
                    </div>
                    <div style={{ color: "#64748b", fontSize: "12px" }}>
                      Licence: {sd.license || "N/A"}
                      {sd.exp ? ` · ${sd.exp} yrs experience` : ""}
                    </div>
                  </div>
                  {sd.phone && (
                    <a href={`tel:${sd.phone}`} className="vc-btn-call-round" title="Call driver">
                      <MdPhone />
                    </a>
                  )}
                </div>

                <div className="vc-location-box">
                  <div className="vc-location-head">
                    <strong style={{ fontSize: "14px", color: "#1e3a8a" }}>
                      📍 Base Hub:{" "}
                      {selectedCab.baseLocation || selectedCab.pickupLocation || selectedCab.fromCity || "N/A"}
                    </strong>
                    <span className="vc-location-badge-40km">
                      🎯 Within {selectedCab.serviceRadiusKm || 40} km Radius
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: "12px", color: "#3b82f6" }}>
                    Eligible for auto-dispatch and ride acceptance within {selectedCab.serviceRadiusKm || 40} km of base location.
                  </p>
                </div>

                <div className="vc-detail-grid">
                  <div>
                    <small>Cab Category</small>
                    <strong>{selectedCab.cabType}</strong>
                  </div>
                  <div>
                    <small>Make & Model</small>
                    <strong>
                      {selectedCab.brand || "Standard"} {selectedCab.model}
                    </strong>
                  </div>
                  <div>
                    <small>Passenger Seating</small>
                    <strong>
                      {selectedCab.availableSeats || 0} / {selectedCab.totalSeats || 4} Available
                    </strong>
                  </div>
                  <div>
                    <small>Base Trip Rate</small>
                    <strong style={{ color: "#059669" }}>₹{selectedCab.price || 0}</strong>
                  </div>
                </div>

                <div className="vc-modal-footer">
                  <button
                    type="button"
                    className="vc-btn-cancel"
                    onClick={() => setSelectedCab(null)}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="vc-btn-submit"
                    onClick={() => editCab(selectedCab)}
                  >
                    ✏️ Edit Vehicle & Driver
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}